import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearTiersData, fetchTiers, setSelectedTiers } from '../reduxStore/TiersSlice';
import { AppDispatch } from '../reduxStore/Store';
import MyTable from '../components/MyTable';
import { ButtonType, TypeTiers } from '../constants/enums';
import { TableHeader, TableAction } from '../constants/interfaces';
import { Tiers } from '../models/models';
import TiersForm from '../components/Forms/TiersForm';
import { DialogTitle, Typography } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import { labels } from '../constants/labels';
import { useDialog } from '../components/DialogComponent';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import TiersFilters, { TiersFilterValues } from '../components/Filters/TiersFilters';

const EMPTY_FILTERS: TiersFilterValues = { nom: null, email: null, telephone: null, categorie_id: null, partage: [] };

/**
 * Gestion des employés — gérés comme des tiers de type EMPLOYE pour bénéficier du
 * suivi compte / balance. (La saisie d'un "dû" via opération diverse est débranchée
 * pour le moment — voir GestionOperationDiverse pour les OD.)
 */
const GestionEmploye: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { tiersData, total, loading, error, successMessage } = useSelector((state: any) => state.TiersSlice);
  const selectedTiers = useSelector((state: any) => state.TiersSlice.selectedTiers);

  const [filters, setFilters] = useState<TiersFilterValues>(EMPTY_FILTERS);
  const [fetchParams, setFetchParams] = useState({ type: TypeTiers.EMPLOYE as string, limit: 5, offset: 0 });

  const [FormDialog, openFormDialog, closeFormDialog] = useDialog({ dialogProps: { maxWidth: 'md', fullWidth: true } });

  useEffect(() => {
    dispatch(fetchTiers({ ...fetchParams, ...filters }));
    return () => {
      dispatch(setSelectedTiers(null));
      dispatch(clearTiersData());
    };
  }, [dispatch, fetchParams, filters, successMessage]);

  const handleFiltersChange = (next: TiersFilterValues) => {
    setFilters(next);
    setFetchParams(prev => ({ ...prev, offset: 0 }));
  };

  const handleFiltersReset = () => {
    setFilters(EMPTY_FILTERS);
    setFetchParams(prev => ({ ...prev, offset: 0 }));
  };

  const handleAdd = () => {
    dispatch(setSelectedTiers(null));
    openFormDialog();
  };

  const handleCancel = () => {
    closeFormDialog();
    dispatch(setSelectedTiers(null));
  };

  const headers: TableHeader<Tiers>[] = [
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'telephone', label: 'Téléphone' },
    { key: 'email', label: 'Email' },
    { key: 'adresse', label: 'Adresse' },
  ];

  const tableActions: TableAction<Tiers>[] = [
    {
      label: 'Edit',
      type: ButtonType.EDIT,
      callback: (employe) => {
        dispatch(setSelectedTiers(employe));
        openFormDialog();
      },
    },
  ];

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setFetchParams(prev => ({ ...prev, offset: newPage * newRowsPerPage, limit: newRowsPerPage }));
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={`Gestion ${labels.employePluriel}`}
        subtitle={`${total} ${labels.employePluriel.toLowerCase()} enregistré${total > 1 ? 's' : ''}`}
        icon={<BadgeIcon />}
        addLabel={`Ajouter un ${labels.employe}`}
        onAdd={handleAdd}
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}
      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface sx={{ mb: 2 }} padding={0}>
        <TiersFilters values={filters} onChange={handleFiltersChange} onReset={handleFiltersReset} />
      </AppSurface>

      <AppSurface>
        <MyTable
          headers={headers}
          data={tiersData}
          onPageChange={handlePageChange}
          actions={tableActions}
          totalRows={total}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedTiers ? `Modifier ${labels.employe}` : `Ajouter un ${labels.employe}`}
          </Typography>
        </DialogTitle>
        <TiersForm onCancel={handleCancel} tiersType={TypeTiers.EMPLOYE} />
      </FormDialog>
    </AppPage>
  );
};

export default GestionEmploye;
